import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { signInAnonymously } from "firebase/auth";
import { AlertCircle, Check, Copy, Download, Loader2, Package } from "lucide-react";
import { toast } from "sonner";

import { auth } from "@/apis/config/firebase";
import { ThemeToggle } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import logoLight from "@/assets/images/logos/logoLightTransparent.png";
import { useMarkInstallLinkUsed } from "@/features/installations/hooks/useInstallLinks";
import {
  MODULES,
  usePreconfiguredDownload,
  type ModuleId,
} from "@/features/installations/hooks/usePreconfiguredDownload";
import { useGetInstallLink } from "@/features/installations/hooks/useInstallLinks";
import type { User } from "@/features/users/schemas/user";

/**
 * Public page for one-time install link access.
 * Route: /install-link/:installId
 * No authentication required — the link itself is the access token.
 */
export default function InstallLink(): React.JSX.Element {
  const { installId } = useParams<{ installId: string }>();
  const [sessionReady, setSessionReady] = useState(() => Boolean(auth.currentUser));
  const { data: link, isLoading, error } = useGetInstallLink(
    sessionReady ? installId : undefined
  );
  const markUsedMutation = useMarkInstallLinkUsed();
  const downloadMutation = usePreconfiguredDownload();

  const [selectedModules, setSelectedModules] = useState<ModuleId[]>([]);
  const [hasDownloaded, setHasDownloaded] = useState(false);

  // Firestore exige sesión. Si el destinatario no está logueado, se abre una
  // sesión anónima antes de leer el documento del enlace.
  useEffect(() => {
    let cancelled = false;

    async function ensureReadableSession() {
      try {
        if (!auth.currentUser) {
          await signInAnonymously(auth);
        }
      } catch (err) {
        console.error("No se pudo crear la sesión anónima del link:", err);
      } finally {
        if (!cancelled) setSessionReady(true);
      }
    }

    void ensureReadableSession();
    return () => {
      cancelled = true;
    };
  }, []);

  // Pre-select modules from the link
  useEffect(() => {
    if (link?.modules) {
      setSelectedModules(link.modules as ModuleId[]);
    }
  }, [link]);

  const handleDownload = async () => {
    if (!link || !institutionUser) return;

    try {
      // El microservicio requiere Bearer; para el link público usamos sesión anónima.
      if (!auth.currentUser) {
        await signInAnonymously(auth);
      }
      const anonToken = await auth.currentUser?.getIdToken(true);
      if (!anonToken) {
        throw new Error("No se pudo obtener el token de sesión");
      }
      localStorage.setItem("authToken", anonToken);

      await downloadMutation.mutateAsync({
        user: institutionUser,
        storageOwnerUid: link.institutionUid,
        modules: selectedModules,
        caddyDomain: link.caddyDomain,
        prefixDicom: link.prefixDicom?.trim() || "",
        installLinkId: installId,
        apiKeyId: link.apiKeyId,
      });

      // 2. Mark as used ONLY after successful download
      await markUsedMutation.mutateAsync(installId!);
      setHasDownloaded(true);
    } catch {
      // If download fails, link remains valid for retry
      toast.error("Error al descargar", {
        description: "No se pudo completar la descarga. Inténtalo de nuevo.",
      });
    }
  };

  const handleCopyLink = async () => {
    if (!installId) return;
    const url = `${window.location.origin}/install-link/${installId}`;
    await navigator.clipboard.writeText(url);
  };

  // Build a User object from the link data for the download hook
  const institutionUser: User = link
    ? {
        uid: link.institutionUid,
        name: link.institutionName,
        email: "",
        type: "institution",
        active: true,
        block_upload: false,
        blood_factor: null,
        blood_type: null,
        category: null,
        address: null,
        admin_imaging: false,
        admin_lab: false,
        api_whatsapp_active: false,
        report_whatsapp_active: false,
        attendances: null,
        created_at: "",
        customized: false,
        days_link: 31,
        dental_images_only: null,
        description: null,
        domain: null,
        edit_report_vm: false,
        email_support: null,
        logo_email: null,
        notification: { email: true, push: false, sms: false, ws: false },
        phone: [],
        social_networks: [],
        schedule: null,
        template_name: "default",
        timezone: "America/Caracas",
        id_tax: null,
        user: link.institutionUid,
        prefix_dicom: "",
        permission_admin: [],
        vm_admin: false,
      }
    : ({} as User);

  const isValid = selectedModules.length > 0;
  const isUsed = link?.used === true;
  const isNotFound = !isLoading && !link && !error;

  // -----------------------------------------------------------------------
  // Loading state
  // -----------------------------------------------------------------------
  if (!sessionReady || isLoading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-gray-400 text-sm">Verificando link...</p>
        </div>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Not found
  // -----------------------------------------------------------------------
  if (isNotFound || error) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="h-16 w-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-gray-100 mb-2">Link no encontrado</h1>
          <p className="text-gray-400 text-sm mb-6">
            El link de instalación que intentas acceder no existe o ha expirado. Contacta
            al administrador para generar uno nuevo.
          </p>
          <Button variant="outline" onClick={handleCopyLink} disabled={!installId}>
            <Copy className="h-4 w-4 mr-2" />
            Copiar este link
          </Button>
        </div>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Already used
  // -----------------------------------------------------------------------
  if (isUsed) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="h-16 w-16 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-amber-500" />
          </div>
          <h1 className="text-xl font-bold text-gray-100 mb-2">Link ya utilizado</h1>
          <p className="text-gray-400 text-sm mb-2">
            Este link de instalación ya fue utilizado para descargar los archivos.
          </p>
          <p className="text-gray-500 text-xs mb-6">
            Los links temporales son de un solo uso. Contacta al administrador para
            generar uno nuevo.
          </p>
          <p className="text-gray-500 text-xs">
            Institución:{" "}
            <strong className="text-gray-300">{link?.institutionName}</strong>
          </p>
        </div>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Valid link — show download form
  // -----------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-gray-950 flex flex-col">
      {/* Header */}
      <header className="border-b border-gray-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={logoLight} alt="ViewMed" className="h-8" />
          <span className="text-gray-400 text-sm">Instalaciones</span>
        </div>
        <ThemeToggle />
      </header>

      {/* Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="max-w-lg w-full space-y-6">
          {/* Card */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-5">
            {/* Header */}
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Package className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-100">
                  Descargar Instalaciones
                </h1>
                <p className="text-sm text-gray-400">{link?.institutionName}</p>
              </div>
            </div>

            {/* Institution info */}
            <div className="bg-gray-800/50 rounded-lg p-3 space-y-1">
              <div className="flex items-center gap-2 text-xs">
                <span className="text-gray-500 w-20 shrink-0">UID:</span>
                <code className="font-mono text-gray-300">{link?.institutionUid}</code>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-gray-500 w-20 shrink-0">Dominio:</span>
                <code className="font-mono text-gray-300">{link?.caddyDomain}</code>
              </div>
            </div>

            {/* Modules (read-only, from link) */}
            <div>
              <p className="text-sm font-medium text-gray-300 mb-2">Módulos incluidos</p>
              <div className="space-y-2">
                {MODULES.map((mod) => {
                  const isSelected = selectedModules.includes(mod.id);
                  return (
                    <label key={mod.id} className="flex items-center gap-3 py-1">
                      <Checkbox checked={isSelected} disabled />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-gray-300">
                          {mod.name}
                        </span>
                        <p className="text-xs text-gray-500">{mod.description}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* CADDY_DOMAIN (read-only, from link) */}
            <div>
              <Label className="text-sm font-medium text-gray-300 mb-1.5 block">
                Dominio Caddy
              </Label>
              <div className="bg-gray-800/50 rounded-md px-3 py-2 text-sm font-mono text-gray-300">
                {link?.caddyDomain}
              </div>
            </div>

            {/* API Key para servicios (read-only, from link) */}
            {link?.apiKeyId && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-300">API Key para servicios</p>
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-500 block">API Key vinculada</Label>
                  <div className="bg-gray-800/50 rounded-md px-3 py-2 text-sm font-mono text-gray-300 truncate">
                    {link.apiKeyName ? `${link.apiKeyName} (${link.apiKeyId})` : link.apiKeyId}
                  </div>
                </div>
              </div>
            )}

            {/* Action */}
            {hasDownloaded ? (
              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 text-center">
                <Check className="h-6 w-6 text-green-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-green-400">Descarga completada</p>
                <p className="text-xs text-green-500/70 mt-1">
                  El link ha sido utilizado y ya no es válido.
                </p>
              </div>
            ) : (
              <Button
                onClick={handleDownload}
                disabled={
                  !isValid || downloadMutation.isPending || markUsedMutation.isPending
                }
                className="w-full h-12 text-base font-bold"
                size="lg"
              >
                {downloadMutation.isPending ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Descargando...
                  </>
                ) : (
                  <>
                    <Download className="h-5 w-5 mr-2" />
                    Descargar Instalaciones
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Footer */}
          <p className="text-center text-xs text-gray-600">Powered by ViewMed</p>
        </div>
      </main>
    </div>
  );
}
